// Pure helpers for the Leavers filter on the admin Users tab.
//
// The server decides who is a leaver (spec §5.1) and sends `leaver_status` only
// to connected_apps.view holders with the connected_apps flag on; for everyone
// else the key is absent. So "any row has the key" is the client's signal that
// the Leavers filter belongs on screen at all.
import { formatDate } from '../../../lib/formatDate';

export const hasLeaverData = (users) => (users || []).some((u) => u && 'leaver_status' in u);

const LEAVER_STATUSES = ['left', 'leaving_soon'];

// Deactivated accounts are never leavers: deactivation is the list's "done".
export const isLeaver = (u) => !!u && u.is_active !== false && LEAVER_STATUSES.includes(u.leaver_status);

// `departure_date` travels as 'YYYY-MM-DD'. new Date('2026-11-15') is UTC
// midnight, which shows the previous day west of Greenwich; build a local date.
const parseYmd = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const seenTime = (u) => (u.odoo_last_seen_at ? new Date(u.odoo_last_seen_at).getTime() : null);

// Left Odoo first, longest-waiting at the top: no last-seen date (left before
// the stamp existed) first, then oldest last seen. Then Leaving soon, nearest
// last day first; an unknown last day goes last.
function compareLeavers(a, b) {
  const rank = (u) => (u.leaver_status === 'left' ? 0 : 1);
  if (rank(a) !== rank(b)) return rank(a) - rank(b);
  if (a.leaver_status === 'left') {
    const at = seenTime(a);
    const bt = seenTime(b);
    if (at !== bt) {
      if (at === null) return -1;
      if (bt === null) return 1;
      return at - bt;
    }
  } else {
    const ad = a.departure_date || '';
    const bd = b.departure_date || '';
    if (ad !== bd) {
      if (!ad) return 1;
      if (!bd) return -1;
      return ad < bd ? -1 : 1;
    }
  }
  return (a.name || '').localeCompare(b.name || '');
}

export const sortLeavers = (users) => [...(users || [])].sort(compareLeavers);

/**
 * Status chip for a leaver row, or null.
 * @returns {{ tone: 'danger'|'warning', label: string, meta: string|null } | null}
 */
export function leaverChip(u, today = new Date()) {
  if (!isLeaver(u)) return null;
  if (u.leaver_status === 'left') {
    return {
      tone: 'danger',
      label: 'Left Odoo',
      meta: u.odoo_last_seen_at ? `Last seen in Odoo ${formatDate(u.odoo_last_seen_at)}` : 'Left before this list started',
    };
  }
  const day = parseYmd(u.departure_date);
  let meta = null;
  if (day) meta = u.departure_date < ymd(today) ? `Last day was ${formatDate(day)}` : `Last day ${formatDate(day)}`;
  return { tone: 'warning', label: 'Leaving soon', meta };
}

// The drawer's Offboard apps entry: a leaver, or a deactivated account when the
// viewer can see leaver data at all (the checklist may still be unfinished).
export const canOffboard = (u) => isLeaver(u) || (!!u && u.is_active === false && 'leaver_status' in u);
